import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import { getProjectsFromAccount } from '../../../../shared/helpers/project/ProjectHelper';
import { PeriodicReportService, PeriodicReportResponse } from '../../../../shared/services/integrations/PeriodicReportService';
import { LookerStudioService } from '../../../../shared/services/integrations/LookerStudioService';
import { format } from 'date-fns';
import { getStatusName } from '../../../../shared/helpers/periodic-reports/PeriodicReportsHelper';
import './PeriodicReportCreate.scss';

interface PeriodicReportCreateProps {
  afterSave: () => void;
  onReportCreated?: (reportId: number) => void;
}

const PeriodicReportCreate: React.FC<PeriodicReportCreateProps> = ({ afterSave, onReportCreated }) => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedReportProvider, setSelectedReportProvider] = useState<string>('LOOKER_STUDIO');
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [createReportError, setCreateReportError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: boolean}>({});
  const [isLookerStudioConnected, setIsLookerStudioConnected] = useState(false);
  const [existingReports, setExistingReports] = useState<PeriodicReportResponse[]>([]);
  const [projectExistingReports, setProjectExistingReports] = useState<PeriodicReportResponse[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);

  const filteredProjects = [...getProjectsFromAccount(accountResponse, projectResponse || [])
    .sort((a: any, b: any) => a.projectLabel.localeCompare(b.projectLabel))];

  const reportProviderOptions = [
    { label: t('lookerStudio'), value: 'LOOKER_STUDIO' },
  ];

  // Helper function to filter project reports
  const getProjectReports = (reports: PeriodicReportResponse[], projectId: number) => {
    return reports.filter(report => 
      report.projectId === projectId && 
      report.provider === 'LOOKER_STUDIO' && 
      report.type === 'DAILY'
    );
  };


  // Check Looker Studio connection status and load existing reports
  useEffect(() => {
    const checkLookerStudioConnection = async () => {
      try {
        const status = await LookerStudioService.getConnectionStatus();
        setIsLookerStudioConnected(status.isConnected);
      } catch (error) {
        setIsLookerStudioConnected(false);
      }
    };

    const loadExistingReports = async () => {
      try {
        setLoadingReports(true);
        const reports = await PeriodicReportService.getPeriodicReports(undefined, 'LOOKER_STUDIO'); // Get Looker Studio reports
        setExistingReports(reports);
      } catch (error) {
        // Error loading reports - will show empty list
      } finally {
        setLoadingReports(false);
      }
    };

    checkLookerStudioConnection();
    loadExistingReports();
  }, []);

  // Update project-specific reports when project is selected
  useEffect(() => {
    if (selectedProject && existingReports.length > 0) {
      const projectReports = getProjectReports(existingReports, selectedProject.id);
      setProjectExistingReports(projectReports);
    } else {
      setProjectExistingReports([]);
    }
  }, [selectedProject, existingReports]);


  const handleCreateReport = async () => {
    if (!filteredProjects || filteredProjects.length === 0) {
      setCreateReportError(t('noProjectsAvailable'));
      return;
    }
    
    if (selectedReportProvider === 'LOOKER_STUDIO' && !isLookerStudioConnected) {
      setCreateReportError(t('notConnectedToLookerStudio'));
      return;
    }
    
    const errors: string[] = [];
    const newValidationErrors: {[key: string]: boolean} = {};
    
    if (!selectedProject) {
      errors.push(t('project'));
      newValidationErrors.project = true;
    }
    
    if (!selectedReportProvider) {
      errors.push(t('provider'));
      newValidationErrors.provider = true;
    }
    
    setValidationErrors(newValidationErrors);
    
    if (errors.length > 0) {
      setCreateReportError(`${t('pleaseFillRequiredFields')}: ${errors.join(', ')}`);
      return;
    }

    // Refresh data just before creating to ensure we have the latest state
    setLoadingReports(true);
    try {
      const freshReports = await PeriodicReportService.getPeriodicReports(undefined, 'LOOKER_STUDIO');
      setExistingReports(freshReports);
      
      // Check again with fresh data
      if (selectedProject) {
        const freshProjectReports = getProjectReports(freshReports, selectedProject.id);
        
        if (freshProjectReports.length > 0) {
          const projectName = selectedProject.projectLabel;
          setCreateReportError(`${t('lookerStudioReportExists')} "${projectName}". ${t('checkReportsList')}`);
          setLoadingReports(false);
          return;
        }
      }
    } catch (error) {
      // Error refreshing reports - continue with stale data
    } finally {
      setLoadingReports(false);
    }

    setIsCreatingReport(true);
    setCreateReportError(null);
    try {
      if (!accountResponse?.accountId) {
        throw new Error('Account ID not available');
      }
      
      if (!selectedProject?.id) {
        setCreateReportError(t('invalidProjectSelected'));
        return;
      }

      // Generate automatic values
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const endDate = new Date();
      const reportName = `${selectedProject.projectLabel} - ${t('dailyReport')}`;
      
      if (selectedReportProvider === 'LOOKER_STUDIO') { // Looker Studio
        // Create both Looker Studio report and periodic report in one call
        const createData = {
          accountId: accountResponse.accountId,
          projectId: selectedProject.id,
          reportName,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          period: 'DAILY'
        };
        
        const response = await LookerStudioService.createReport(createData);

        if (response?.data) {
          // LookerStudioService.createReport already creates the periodic report
          // No need to create it again - just call afterSave to refresh the list
          const reportId = response.data.id;
          
          // Call the callback to open report configuration dialog
          if (onReportCreated && reportId) {
            onReportCreated(reportId);
          } else {
            afterSave();
          }
        }
      } else {
        // For other providers, create periodic report directly
        const reportData = {
          projectId: selectedProject.id,
          provider: selectedReportProvider,
          type: 'DAILY', // Daily report type
          config: {
            parameters: {
              reportName,
              dateRange: 'last_30_days',
              period: 'daily'
            }
          },
          schedule: {
            hour: 9,
            timezone: 'UTC'
          },
          status: 'ACTIVE'
        };
        
        await PeriodicReportService.createPeriodicReport(reportData);
        afterSave();
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 'INSUFFICIENT_SCOPES') {
        setCreateReportError(t('googlePermissionsChanged'));
        setIsLookerStudioConnected(false);
      } else if (error?.response?.data?.code === 'REPORT_ALREADY_EXISTS') {
        setCreateReportError(t('lookerStudioReportExists'));
      } else if (error?.response?.data?.error) {
        setCreateReportError(`${t('failedToCreateReport')}: ${error.response.data.error}`);
      } else {
        setCreateReportError(t('failedToCreateReport'));
      }
    } finally {
      setIsCreatingReport(false);
    }
  };

  return (
    <div className="periodic-report-create">
      <form className="form">
        <div className="form__row">
          <label className="form__label" htmlFor="provider">{t('provider')}</label>
          <Dropdown
            id="provider"
            value={selectedReportProvider}
            options={reportProviderOptions}
            onChange={(e) => {
              setSelectedReportProvider(e.value);
              if (validationErrors.provider) {
                setValidationErrors(prev => ({ ...prev, provider: false }));
              }
            }}
            optionLabel="label"
            placeholder={t('selectProvider')}
            className={`dropdown ${validationErrors.provider ? 'p-invalid' : ''}`}
            disabled={true} // Only Looker Studio for now
          />
          {validationErrors.provider && <small className="p-error">{t('providerRequired')}</small>}
        </div>
        
        <div className="form__row">
          <label className="form__label" htmlFor="project">{t('project')}</label>
          <Dropdown
            id="project"
            value={selectedProject}
            options={filteredProjects || []}
            onChange={(e) => {
              setSelectedProject(e.value);
              if (validationErrors.project) {
                setValidationErrors(prev => ({ ...prev, project: false }));
              }
            }}
            optionLabel="projectLabel"
            placeholder={t('selectProject')}
            className={`dropdown dropdown--with-clear ${validationErrors.project ? 'p-invalid' : ''}`}
            disabled={!filteredProjects || filteredProjects.length === 0}
            filter
            filterPlaceholder={t('searchProjects')}
            filterBy="projectLabel"
            showClear
          />
          {validationErrors.project && <small className="p-error">{t('projectRequired')}</small>}
        </div>
        
        {/* Show loading state */}
        {selectedProject && loadingReports && (
          <div className="form__row">
            <div className="form__loading">
              {t('loadingExistingReports')}...
            </div>
          </div>
        )}

        {/* Show existing reports for selected project */}
        {selectedProject && !loadingReports && projectExistingReports.length > 0 && (
          <div className="form__row">
            <div className="form__warning">
              <p className="form__warning__title">
                ⚠️ {t('existingReportsForProject')}:
              </p>
              <ul className="form__warning__list">
                {projectExistingReports.map((report) => (
                  <li key={report.id}>
                    {report.config?.parameters?.reportName || `${selectedProject.projectLabel} - ${t('dailyReport')}`}
                    {' '} ({t('status')}: {getStatusName(report.status, report.provider)})
                  </li>
                ))}
              </ul>
              <p className="form__warning__note">
                {t('onlyOneLookerStudioReportAllowed')}
              </p>
            </div>
          </div>
        )}

        
        <div className="form__row">
          <div className="form__info">
            <p><strong>{t('reportWillBeCreatedWith')}:</strong></p>
            <ul>
              <li>{t('name')}: [{t('projectName')}] - {t('dailyReport')}</li>
              <li>{t('dateRange')}: {t('last30Days')}</li>
              <li>{t('period')}: {t('daily')}</li>
            </ul>
          </div>
        </div>
      </form>
      
      {createReportError && (
        <div className="form__error">
          <div className="p-error">
            {createReportError}
          </div>
        </div>
      )}
      
      <div className="dialog-box__actions">
        <Button 
          label={isCreatingReport ? t('creating') : t('createReport')}
          onClick={handleCreateReport}
          disabled={isCreatingReport || loadingReports || (selectedProject && projectExistingReports.length > 0)}
          className="btn"
        />
      </div>
    </div>
  );
};

export default PeriodicReportCreate;
